import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Terminal } from 'features/routing/types/terminals';

import { TAuthTaskStatus, TSignInDTO, TSignUpDTO, TUser } from '../types';

export type TAuthState = {
  userDetails?: TUser;
  accessToken?: string;
  authTaskStatus: TAuthTaskStatus;
  signUpSuccess: boolean;
  error: string;
  resetTokenEmail: string;
  successMsg: string;
};

const createAuthSlice = (initialState: TAuthState) =>
  createSlice({
    name: 'user',
    initialState,
    reducers: {
      signIn(
        state,
        { payload }: PayloadAction<{ user: TUser; token: string }>
      ) {
        state.accessToken = payload.token;
        state.userDetails = payload.user;
        state.authTaskStatus = 'idle';
      },
      startAuthTask(state) {
        state.authTaskStatus = 'pending';
      },
      endAuthTask(state) {
        state.authTaskStatus = 'idle';
      },
      signOut(state) {
        state.userDetails = undefined;
        state.accessToken = undefined;
      },
      onError(state, { payload }: PayloadAction<string>) {
        state.error = payload;
      },
      clearError(state) {
        state.error = '';
      },
      signUpSuccess(state) {
        state.signUpSuccess = true;
      },
      clearSignUpSuccess(state) {
        state.signUpSuccess = false;
      },
      onResetTokenEmail(state, { payload }: PayloadAction<string>) {
        state.resetTokenEmail = payload;
      },
      clearResetTokenEmail(state) {
        state.resetTokenEmail = '';
      },
      onSuccess(state, { payload }: PayloadAction<string>) {
        state.successMsg = payload;
      },
      clearSucces(state) {
        state.successMsg = '';
      },
      setProfileImage(state, { payload }: PayloadAction<string>) {
        if (state.userDetails) {
          state.userDetails.profileImage = payload;
        }
      },
      setUsers(state, { payload }: PayloadAction<TUser>) {
        state.userDetails = payload;
      },

      setTopDestinations(state, { payload }: PayloadAction<Terminal[]>) {
        if (state.userDetails) {
          state.userDetails.topDestinations = payload;
        }
      },
    },
  });

const authSlice = createAuthSlice({
  authTaskStatus: 'idle',
  error: '',
  signUpSuccess: false,
  resetTokenEmail: '',
  successMsg: '',
});

export const signInRequest = createAction<TSignInDTO>('signInRequest');
export const signUpRequest = createAction<TSignUpDTO>('signUpRequest');
export const recoverPasswordRequest = createAction<string>(
  'recoverPassordRequest'
);
export const updatePasswordRequest = createAction<TSignInDTO>(
  'updatePasswordRequest'
);
export const validateTokenRequest = createAction<string>(
  'validateTokenRequest'
);
export const verifyAccountRequest = createAction<string>(
  'verifyAccountRequest'
);
export const resendPasswordResetEmailRequest = createAction<string>(
  'resendPasswordResetEmailRequest'
);
export const resendVerifyAccountEmailRequest = createAction<string>(
  'resendVerifyAccountEmailRequest'
);
export const signOutRequest = createAction('signOutRequest');
export const cancelAuthTask = createAction('cancelAuthTask');

export const {
  endAuthTask,
  signIn,
  startAuthTask,
  signOut,
  onError,
  clearError,
  clearSignUpSuccess,
  signUpSuccess,
  clearResetTokenEmail,
  onResetTokenEmail,
  clearSucces,
  onSuccess,
  setProfileImage,
  setUsers,
  setTopDestinations,
} = authSlice.actions;

export const authBlacklistSync = [
  onError.type,
  endAuthTask.type,
  startAuthTask.type,
  clearError.type,
  clearSignUpSuccess.type,
  signUpSuccess.type,
  clearResetTokenEmail.type,
  onResetTokenEmail.type,
  clearSucces.type,
  onSuccess.type,
  cancelAuthTask.type,
  signInRequest.type,
  signUpRequest.type,
  recoverPasswordRequest.type,
  updatePasswordRequest.type,
  validateTokenRequest.type,
  verifyAccountRequest.type,
  resendPasswordResetEmailRequest.type,
  resendVerifyAccountEmailRequest.type,
  cancelAuthTask.type,
];

export default authSlice.reducer;
